import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as companyGroupService from "../../../models/services/companyGroupService";
import { ActionContainer } from "../../framework/ActionContainer";
import { Dialog } from "../../framework/Dialog/Dialog";
import { List } from "../../framework/List/List";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Box, FormControlLabel, Grid, Radio } from "@mui/material";
import { CompanyGroupType } from "ui/Scripts/source/models/enums";
import { CompanyGroupForPermissionListItem, ICompanyGroupForPermissionListItem } from "source/models/companyGroup/companyGroupForPermissionListItem";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface ISelectCompanyGroupForPermissionsDialogProps {
    companyId: string;
    title: string;
    ignoreCompanyGroupCodes?: string[];
    multiSelect?: boolean;
    canUseAllCompanyGroup?: boolean;
    hasNormalCompanyGroups: boolean;
    hasFixedCompanyGroups: boolean;
    messageTargetMode?: boolean;
    onOk: (userGroups: ICompanyGroupForPermissionListItem[]) => void;
    onCancel: () => void;
}

export interface ISelectCompanyGroupForPermissionsDialogState {
    intialized: boolean;
    isLoading: boolean;
    groupType: CompanyGroupType;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: ICompanyGroupForPermissionListItem[];
    selectedId: string;
    checkedIds: string[];
}

export class SelectCompanyGroupForPermissionsDialog extends React.Component<ISelectCompanyGroupForPermissionsDialogProps, ISelectCompanyGroupForPermissionsDialogState> {
    constructor(props: ISelectCompanyGroupForPermissionsDialogProps) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            groupType: props.hasNormalCompanyGroups && props.hasNormalCompanyGroups
                ? CompanyGroupType.Normal
                : CompanyGroupType.Fixed,
            page: 1,
            hasMore: true,
            filter: "",
            sortColumn: "name",
            sortOrderIsAsc: true,
            items: [],
            selectedId: null,
            checkedIds: [],
        };
    }

    searchItems = async (groupType: CompanyGroupType, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean) => {
        const props = this.props;
        const state = this.state;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => companyGroupService.getCompanyGroupForPermissionListItems(props.companyId, !refreshList ? infiniteListPageSize : (page * infiniteListPageSize),
            !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc, groupType, props.ignoreCompanyGroupCodes, props.messageTargetMode), false, true, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        if (groupType !== CompanyGroupType.Fixed && props.canUseAllCompanyGroup && items.items.findIndex(i => i.code === "") < 0 && props.ignoreCompanyGroupCodes.findIndex(i => i === "") < 0) {
            //console.log(props.canUseAllCompanyGroup, items.items, props.ignoreCompanyGroupCodes, props.ignoreCompanyGroupCodes.findIndex(i => i === ""));
            items.items.unshift(CompanyGroupForPermissionListItem.createCompanyGroupForPermissionListItem(Base.emptyGuid, "", "'" + Translations.AllCompanies + "'"));
        }
        this.setState({
            intialized: true,
            groupType,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId: items.selectedId,
            checkedIds: items.checkedIds,
        });
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.groupType, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    componentDidUpdate(prevProps: ISelectCompanyGroupForPermissionsDialogProps, prevState: ISelectCompanyGroupForPermissionsDialogState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId && Base.equalStrArrays(prevProps.ignoreCompanyGroupCodes, props.ignoreCompanyGroupCodes)) return;
        this.searchItems(state.groupType, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.groupType, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    changeGroupType = (groupType: CompanyGroupType) => {
        const state = this.state;
        if (state.groupType === groupType) return;
        this.searchItems(groupType, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.groupType, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    handleCancel = () => {
        this.props.onCancel();
    };

    handleOk = () => {
        const state = this.state;
        const ids = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (ids.length < 1) return;
        const items = state.items.filter(i => ids.indexOf(i.id) > -1);
        if (items.length < 1) return;
        this.props.onOk(items);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="selectCompanyGroupForPermissionsDialog"
                maxWidth="md"
                maxHeight="726px"
                title={props.title}
                body={
                    <VerticalContainer
                        before={<>
                            {props.hasNormalCompanyGroups && props.hasNormalCompanyGroups &&
                                <GridRow margin="-16px 0 0 -16px">
                                    <Grid item sm={6}>
                                        <FormControlLabel
                                            label={Translations.CompanyGroups}
                                            control={
                                                <Radio
                                                    checked={state.groupType < 0.5}
                                                    onChange={(_e, checked: boolean) => {
                                                        this.changeGroupType(checked ? CompanyGroupType.Normal : CompanyGroupType.Fixed);
                                                    }}
                                                />
                                            }
                                        />
                                    </Grid>
                                    <Grid item sm={6}>
                                        <FormControlLabel
                                            label={Translations.Companies}
                                            control={
                                                <Radio
                                                    checked={state.groupType > 0.5}
                                                    onChange={(_e, checked: boolean) => {
                                                        this.changeGroupType(checked ? CompanyGroupType.Fixed : CompanyGroupType.Normal);
                                                    }}
                                                />
                                            }
                                        />
                                    </Grid>
                                </GridRow>
                            }
                            <ActionContainer
                                leftItems={
                                    <ListSearchFilter
                                        searchFilter={state.filter}
                                        onSearchClick={this.changeFilter}
                                    />
                                }
                            />
                        </>}
                        main={
                            <List
                                multiSelect={props.multiSelect}
                                intialized={state.intialized}
                                isLoading={state.isLoading}
                                hasMore={state.hasMore}
                                height="100%"
                                header={
                                    {
                                        columns: [
                                            { size: 6, column: "name", content: Translations.Name },
                                            { size: 6, column: "comment", content: Translations.Description }
                                        ]
                                    }
                                }
                                lines={
                                    state.items.map((item) => {
                                        return {
                                            columns: [
                                                {
                                                    size: 6,
                                                    column: "name",
                                                    content: state.groupType === CompanyGroupType.Fixed
                                                        ? <Box display="flex" sx={{ overflow: "hidden" }}>
                                                            <AvatarImage
                                                                name={item.name}
                                                                image={item.logo}
                                                                tooltip={item.name}
                                                                marginRight=".5rem"
                                                            />
                                                            <span>{item.name}</span>
                                                        </Box>
                                                        : item.name
                                                },
                                                { size: 6, column: "comment", content: item.comment }
                                            ],
                                            id: item.id
                                        };
                                    })}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                            />
                        }
                    />
                }
                buttons={[
                    { title: Translations.Cancel, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleOk },
                ]}
                onClose={this.handleCancel}
            />
        );
    }
}